import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { forkJoin, of } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';

import {
    DEFAULT_RECENTLY_ACTIVE,
    DEFAULT_RECENTLY_ACTIVE_DAYS,
    DEFAULT_RECENTLY_CLOSED,
    DEFAULT_RECENTLY_CLOSED_DAYS,
    DEFAULT_TEAM_FOCUSES,
    DEFAULT_TEAM_RECENTLY_ACTIVE_DAYS,
    DEFAULT_TEAM_RECENTLY_CLOSED_DAYS,
    FocusSelectionParams,
} from '../../coaching-base/config';
import { CommitmentService } from '../../coaching-commitment/services/commitment.service';
import { Dependencies } from '../../store';
import { PayloadAction } from '../../store.model';

import { ActionType, TagSetInitAction, tagSetInitResult } from './tags.action';
import { ChainGroup, dedup } from './tags.function';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const tagSetInit = (
    action: ActionsObservable<PayloadAction<ActionType, TagSetInitAction>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.COACHING_TAG_SET__INIT),
        switchMap((act) => {
            const service =
                deps.injector.get<CommitmentService>(CommitmentService);
            const grp: ChainGroup<TagSetInitAction> = { root: act.payload };

            return of(null).pipe(
                concatMap(() => {
                    return forkJoin([
                        of(null).pipe(
                            service.listFocuses(() => {
                                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                                const params =
                                    act.payload.focusSelectionParams ||
                                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                                    ({} as FocusSelectionParams);
                                return {
                                    userId: act.payload.performerId,
                                    recentlyActive:
                                        params.recentlyActive != null
                                            ? params.recentlyActive
                                            : DEFAULT_RECENTLY_ACTIVE,
                                    recentlyActiveDays:
                                        params.recentlyActiveDays != null
                                            ? params.recentlyActiveDays
                                            : DEFAULT_RECENTLY_ACTIVE_DAYS,
                                    recentlyClosed:
                                        params.recentlyClosed != null
                                            ? params.recentlyClosed
                                            : DEFAULT_RECENTLY_CLOSED,
                                    recentlyClosedDays:
                                        params.recentlyClosedDays != null
                                            ? params.recentlyClosedDays
                                            : DEFAULT_RECENTLY_CLOSED_DAYS,
                                    teamFocuses:
                                        params.teamFocuses != null
                                            ? params.teamFocuses
                                            : DEFAULT_TEAM_FOCUSES,
                                    teamRecentlyActiveDays:
                                        params.teamRecentlyActiveDays != null
                                            ? params.teamRecentlyActiveDays
                                            : DEFAULT_TEAM_RECENTLY_ACTIVE_DAYS,
                                    teamRecentlyClosedDays:
                                        params.teamRecentlyClosedDays != null
                                            ? params.teamRecentlyClosedDays
                                            : DEFAULT_TEAM_RECENTLY_CLOSED_DAYS,
                                };
                            }),
                            tap((ret) => {
                                grp.focusSummarySet = ret;
                                // TODO move to reducer
                                grp.behaviours = dedup(
                                    ret.userBehaviours || [],
                                    (e) => e.behaviourId,
                                ).map((e) => ({
                                    id: e.behaviourId,
                                    initials: e.behaviourName,
                                    shortName: e.behaviourName,
                                    name: e.behaviourName,
                                }));
                                grp.subBehaviours = dedup(
                                    ret.userBehaviours || [],
                                    (e) => e.subBehaviourId,
                                ).map((e) => ({
                                    id: e.subBehaviourId,
                                    initials: e.subBehaviourName,
                                    shortName: e.subBehaviourName,
                                    name: e.subBehaviourName,
                                    behaviourId: e.behaviourId,
                                }));
                                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                                grp.activeCommitmentGoalMetrics =
                                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                                    ret.userActiveCommitmentGoalMetrics &&
                                    ret.userActiveCommitmentGoalMetrics.reduce<
                                        number[]
                                    >((p, c) => {
                                        if (c.goalMetricIds) {
                                            p.push(...c.goalMetricIds);
                                        }
                                        return p;
                                    }, []);
                            }),
                        ),
                    ]);
                }),
                map(() =>
                    tagSetInitResult(
                        {
                            ...act.payload,
                            focusSummarySet: grp.focusSummarySet,
                            behaviours: grp.behaviours,
                            subBehaviours: grp.subBehaviours,
                            activeCommitmentGoalMetrics:
                                grp.activeCommitmentGoalMetrics,
                        },
                        act.storePath,
                    ),
                ),
                catchError((err) => {
                    console.log(err);
                    return of(
                        tagSetInitResult({ ...act.payload }, act.storePath),
                    );
                }),
            );
        }),
    );

export default [tagSetInit];
